import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as api from '../../../../api';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import Form from '../../../../components/fields/Form';
import ProfileImageUploadField from '../../../../components/fields/ProfileImageUploadField';
import { type UserFormValues } from '../../../../pages/admin/UserSelfPage/General/ProfileDetails';
import useHandleUserAssets from '../../../../pages/admin/UserSelfPage/General/useHandleUserAssets';
import { useThemeComponents } from '../../../../themes';
import { allowedExtensions } from '../../../../utilities/extensions';
import { hasValue } from '../../../../utilities/fp';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import { AvatarContainer } from '../styled';

const formValidator = validators.compose(
    validators.requiredString('displayName'),
    validators.requiredString('email'),
    validators.validEmail('email'),
    validators.only(
        values => hasValue('mobile.value')(values) || hasValue('mobile.prefix')(values),
        validators.compose(
            validators.validPhone('mobile.value', 'mobile.prefix'),
            validators.requiredNumber('mobile.prefix')
        )
    )
);

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px;
`;

const StyledForm = styled(Form)`
    width: 100%;
    & .ant-input-disabled {
        background-color: transparent;
    }
`;

const EditProfileContent = () => {
    const { t } = useTranslation(['userSelf']);
    const user = useAccount();
    const company = useCompany();
    const companyId = company?.id;
    const {
        FormFields: { InputField, PhoneAndPrefixField },
    } = useThemeComponents();

    const initialValues = useMemo(
        (): UserFormValues => ({
            displayName: user.displayName,
            email: user.email,
            mobile: user.mobile,
            profileImage: user.profileImage,
            alias: user.alias,
        }),
        [user]
    );

    const apolloClient = useApolloClient();
    const handleUserAssets = useHandleUserAssets();

    const onSubmit = useHandleError<UserFormValues>(
        async values => {
            const { profileImage, ...data } = values;

            // submitting message
            message.loading({
                content: t('userSelf:general.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit update
            await apolloClient
                .mutate<api.UpdateAccountMutation, api.UpdateAccountMutationVariables>({
                    mutation: api.UpdateAccountDocument,
                    variables: { id: user.id, data, companyId },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // handle user asset upload/delete
            await handleUserAssets(user.id, values, user);

            // inform about success
            message.success({
                content: t('userSelf:general.messages.updateSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, user, handleUserAssets, t, companyId]
    );

    const validate = useValidator(formValidator);

    return (
        <Container>
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <StyledForm id="editProfileForm" name="editProfileForm" onSubmitCapture={handleSubmit}>
                        <AvatarContainer>
                            <ProfileImageUploadField
                                key="profileImage"
                                extensions={allowedExtensions.image}
                                name="profileImage"
                                placeHolderText={t('userSelf:editMyProfile.changeButton')}
                            />
                        </AvatarContainer>
                        <InputField
                            {...t('userSelf:general.fields.displayName', {
                                returnObjects: true,
                            })}
                            name="displayName"
                            required
                        />
                        <InputField label="Role" name="role" placeholder="Administrator" disabled />
                        <InputField
                            {...t('userSelf:general.fields.email', {
                                returnObjects: true,
                            })}
                            name="email"
                            required
                        />
                        <InputField
                            {...t('userSelf:general.fields.alias', {
                                returnObjects: true,
                            })}
                            name="alias"
                        />

                        <PhoneAndPrefixField
                            {...t('userSelf:general.fields.mobile', {
                                returnObjects: true,
                            })}
                            name="mobile"
                        />
                    </StyledForm>
                )}
            </Formik>
        </Container>
    );
};

export default EditProfileContent;
